import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * One item on the global Market Ticker. Mirrors `TickerItem` from
 * `@arcanum-hq/shared-types`. apps/web does NOT import Zod schemas from
 * shared-types (pure types only); the runtime shape is guaranteed by the
 * API envelope contract and locked in via the hook-level test.
 */
export interface TickerItem {
  category: 'equities' | 'crypto' | 'fx' | 'commodities' | 'bonds';
  symbol: string;
  price: number;
  change_abs: number | null;
  change_pct: number | null;
  as_of: string;
  source: 'twelve_data' | 'coingecko' | 'frankfurter' | 'fred' | 'metals_api';
  unit?: string;
}

export interface TickerPayload {
  items: TickerItem[];
  fetched_at: string;
  categories_available: string[];
  warnings: string[];
}

interface ApiTickerResponse {
  success: boolean;
  data: TickerPayload;
}

/**
 * Global Market Ticker — 21 items across 5 categories. Server pre-warms the
 * cache every 10 minutes (Cloud Scheduler → /refresh/ticker), so user reads
 * always hit a warm 15-min cache. 4-min client staleTime + 5-min
 * refetchInterval keeps the visible ticker close to the server state without
 * chasing every cache flip.
 */
export function useMarketTicker() {
  return useQuery<TickerPayload>({
    queryKey: queryKeys.marketTicker.payload(),
    queryFn: async () => {
      const res = await api.get<ApiTickerResponse>('/api/market-data/ticker');
      return res.data;
    },
    staleTime: 4 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    // refetchOnWindowFocus removed 2026-05-27 — the 5-min interval already
    // keeps the ticker fresh; on-focus refetch was part of the thundering
    // herd that <FocusResumeCoordinator> replaces.
    retry: 1,
  });
}
